import React, { useState, useEffect } from 'react';
import { useTimeOffRequests } from '../../context/TimeOffRequestsContext';
import { Link } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, CalendarIcon, ClockIcon, UserGroupIcon, EyeIcon } from '@heroicons/react/24/outline';
import Pagination from '../common/Pagination';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
 
const TimeOffRequestsList = () => {
  const { timeOffRequests, deleteTimeOffRequest, getTimeOffRequests } = useTimeOffRequests();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();
  const [showAddBtn, setShowAddBtn] = useState(true);
  const [showDeleteBtn, setShowDeleteBtn] = useState(true);
  const [filteredTimeOffRequests, setFilteredTimeOffRequests] = useState([]);
  const getRoutePath = useRoutePath();  
  const [totalPages, setTotalPages] = useState(1);
  const [showMoreAssignToModal, setShowMoreAssignToModal] = useState({
    show: false,
    data: []
  });
   
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc'
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get('page')) || 1;
    setCurrentPage(page)
    
    if(user.role_id !== 1 && timeOffRequests?.data?.length >= 1){
      setShowAddBtn(true)
    }
    if(user.role_id !== 1){
      setShowDeleteBtn(true)
    }
 
    setSearchTerm(params.get('q') || '')
    setStatusFilter(params.get('active') || '')
    setTypeFilter(params.get('type') || 'all')

  },[user, timeOffRequests, showAddBtn])

   
  const filtersData = (key, value, arrayFilters = []) => {
    const applyFilters = async (filterDataVal) => {
      let filters = {};
      const newUrl = new URL(window.location);
      
      if (filterDataVal['q']?.key === 'q') {
        filters.q = filterDataVal['q']?.value;
        if(filterDataVal['q']?.value){
          setSearchTerm(filterDataVal['q'].value)
          newUrl.searchParams.set('q', filterDataVal['q'].value);
        }else{
          newUrl.searchParams.delete('q');
        }
      }else if(searchTerm){
        filters.q = searchTerm;
        newUrl.searchParams.set('q', searchTerm);
      } else{
        newUrl.searchParams.delete('q');
      }
  
      if(filterDataVal['active']?.key === 'active'){
        filters.active = filterDataVal['active']?.value;
        if(filterDataVal['active']?.value){
          setStatusFilter(filterDataVal['active'].value)
          newUrl.searchParams.set('active', filterDataVal['active'].value);
        }else{
          newUrl.searchParams.delete('active');
        }
      }else if(statusFilter){
        filters.active = statusFilter;
        newUrl.searchParams.set('active', statusFilter);
      } else{
        newUrl.searchParams.delete('active');
      }

      if(filterDataVal['type']?.key === 'type'){
        filters.type = filterDataVal['type']?.value;
        if(filterDataVal['type']?.value && filterDataVal['type']?.value !== 'all'){
          setTypeFilter(filterDataVal['type'].value)
          newUrl.searchParams.set('type', filterDataVal['type'].value);
        }else{
          newUrl.searchParams.delete('type');
        }
      }else if(typeFilter && typeFilter !== 'all'){
        filters.type = typeFilter;
        newUrl.searchParams.set('type', typeFilter);
      } else{
        newUrl.searchParams.delete('type');
      }

      newUrl.searchParams.delete('page');
      window.history.pushState({}, '', newUrl);
      
      const params = new URLSearchParams(window.location.search);
      const page = parseInt(params.get('page')) || 1;
      if(Object.keys(filters).length > 0){
        let response = await getTimeOffRequests(page, filters);
       
        if(response?.data){
          setTotalPages(response?.last_page ? response.last_page : 1)
          setFilteredTimeOffRequests(response?.data);
          setCurrentPage(page)
        }
      }
    };
 
    let filterDataVal = {};
    if(arrayFilters.length > 0){
      arrayFilters.forEach(filter => {
        filterDataVal[filter.key] = {
          key:filter.key,value:filter.value
        } 
      })
    }else{
      filterDataVal[key] = {
        key:key,value:value
      } 
    }
    applyFilters(filterDataVal);
  }

  useEffect(() => {
     
    setTotalPages(timeOffRequests?.last_page ? timeOffRequests.last_page : 1)
    
    if (timeOffRequests?.data) {
      let filtered = [...timeOffRequests.data];
       
      // Apply sorting
      if (sortConfig.key) {
        filtered.sort((a, b) => {
          let aValue = a[sortConfig.key];
          let bValue = b[sortConfig.key];
          
          if (sortConfig.key.includes('.')) {
            const fields = sortConfig.key.split('.');
            aValue = fields.reduce((obj, field) => obj?.[field], a);
            bValue = fields.reduce((obj, field) => obj?.[field], b);
          }

          if (aValue === undefined || aValue === null) aValue = '';
          if (bValue === undefined || bValue === null) bValue = '';

          if (typeof aValue === 'boolean') {
            return sortConfig.direction === 'asc' ? (aValue ? 1 : -1) : (aValue ? -1 : 1);
          }

          if (sortConfig.key === 'created_at' || sortConfig.key === 'updated_at') {
            const dateA = new Date(aValue);
            const dateB = new Date(bValue);
            return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
          }

          return sortConfig.direction === 'asc'
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        });
      }
     
      setFilteredTimeOffRequests(filtered);
    }
  }, [timeOffRequests, searchTerm, statusFilter, typeFilter, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Calculate pagination using time off requests values
  const currentItems = filteredTimeOffRequests;
  const handlePageChange = (page) => {
    setCurrentPage(page);
    let filters = {}; 
    if(searchTerm){
      filters.q = searchTerm;
    }
    if(statusFilter){
      filters.active = statusFilter;
    }
    if(typeFilter && typeFilter !== 'all'){
      filters.type = typeFilter;
    }
     
    getTimeOffRequests(page, filters);
    // Update the URL with ?page={page}
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('page', page);
    window.history.pushState({}, '', newUrl);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        deleteTimeOffRequest(id).then(response => {
          if(response.status === 200){
            Swal.fire(
              'Deleted!',
              'Time Off Request has been deleted.',
              'success'
            );
          }else{
            Swal.fire(
              'Error!',
              'Time Off Request has not been deleted.',
              'error'
            );
          }
        });
      }
    });
  };

  const SortableHeader = ({ columnKey, children }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
      onClick={() => handleSort(columnKey)}
    >
      <div className="flex items-center">
        {children}
        {sortConfig.key === columnKey && (
          <span className="ml-1">
            {sortConfig.direction === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  const getStatusBadge = (status, type) => {
    if (type === 'active') {
      return (
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
          status === 1 || status === true
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {status === 1 || status === true ? 'Active' : 'Inactive'}
        </span>
      );
    } else if (type === 'completed') {
      return (
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
          status === 1 || status === true
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-gray-100 text-gray-600'
        }`}>
          {status === 1 || status === true ? 'Completed' : 'Pending'}
        </span>
      );
    } else if (type === 'recurring') {
      return (
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
          status === 1 || status === true
            ? 'bg-purple-100 text-purple-800' 
            : 'bg-gray-100 text-gray-600'
        }`}>
          {status === 1 || status === true ? 'Recurring' : 'One-time'}
        </span>
      );
    }
  };
 
  
  return (
    <div className="py-6">
      <div className="max-w-10xl mx-auto px-4 sm:px-6 md:px-8">
        <Breadcrumb/>
        <div className="mb-3">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-gray-900">Time Off Requests</h1>
          {showAddBtn && (
            <Link 
              to={getRoutePath('/time-off-requests/create')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add New Time Off Request
            </Link>
          )}
        </div>
      </div>
      </div>    
      <div className="max-w-10xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="bg-white rounded-lg shadow-md mb-3">
          <div className="mb-4 grid grid-cols-1 md:grid-cols-3 p-5 gap-4">
            <div>
              <input
                type="text"
                placeholder="Search time off requests..."
                value={searchTerm}
                onChange={(e) => filtersData('q', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => filtersData('active', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
            
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Time Off Requests</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Manage and track time off requests for your team.</p>
          </div>
          <div className="border-t border-gray-200">
            <div className="overflow-x-auto scrollbar-thin">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader columnKey="time_off_request_type.name">Request Type</SortableHeader>
                    <SortableHeader columnKey="assign_to_ids">Assigned To</SortableHeader>
                    <SortableHeader columnKey="start_date">Start Date</SortableHeader>
                    <SortableHeader columnKey="end_date">End Date</SortableHeader>
                    <SortableHeader columnKey="end_time">End Time</SortableHeader>
                    <SortableHeader columnKey="recurring">Recurring</SortableHeader>
                    <SortableHeader columnKey="active">Status</SortableHeader>
                    <SortableHeader columnKey="completed">Completion</SortableHeader>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="px-6 py-4 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <PlusIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Time Off Request Found</h3>
                          <p className="text-gray-500 mb-4">Get started by adding a new time off request</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                  ) : (
                    currentItems.map((timeOffRequest) => (
                      <tr key={timeOffRequest.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-gray-200 mr-3 flex items-center justify-center">
                              <span className="text-gray-500 text-sm">
                                {timeOffRequest?.time_off_request_type?.name?.[0]?.toUpperCase() || 'T'}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {timeOffRequest?.time_off_request_type?.name || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {timeOffRequest.formated_created_at ? timeOffRequest.formated_created_at : 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {timeOffRequest?.assign_to && Array.isArray(timeOffRequest?.assign_to) && timeOffRequest?.assign_to.length > 0 ? (
                              <div className="space-y-1">
                                {timeOffRequest.assign_to.slice(0, 2).map((value, index) => (
                                  <div key={index} className="flex items-center">
                                    <UserGroupIcon className="w-4 h-4 text-gray-400 mr-1" />
                                    <Link
                                      to={getRoutePath(`/company-workforces/edit/${value?.id}`)}
                                      className="text-[#009efb] hover:text-[#0089db] text-sm"
                                    >
                                      {value?.first_name} {value?.last_name}
                                    </Link>
                                  </div>
                                ))}
                                {timeOffRequest.assign_to.length > 2 && (
                                  <div className="text-xs text-gray-500 cursor-pointer" onClick={() => setShowMoreAssignToModal({show: true, data: timeOffRequest})}>
                                    +{timeOffRequest.assign_to.length - 2} more
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">Unassigned</span>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <CalendarIcon className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {formatDate(timeOffRequest.start_date)}
                            </span>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <CalendarIcon className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {formatDate(timeOffRequest.end_date)}
                            </span>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <ClockIcon className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {formatTime(timeOffRequest.end_time)}
                            </span>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-normal">
                          <span
                            className={`break-words max-w-[150px] sm:max-w-[200px] md:max-w-[300px] px-2 inline-block text-xs leading-5 font-semibold rounded-full ${
                              timeOffRequest.recurring === 1 || timeOffRequest.recurring
                                ? 'text-green-800'
                                : 'text-gray-800 font-bold'
                            }`}
                          >
                            {timeOffRequest.recurring === 1 ? timeOffRequest.recurring_note : 'No Recurring'}
                          </span>
                        </td>


                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(timeOffRequest.active, 'active')}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                           <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            timeOffRequest.completed === 1 || timeOffRequest.completed 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            { timeOffRequest.completed === 1 ? 'Yes' : 'No' }
                          </span>
                        </td>
  
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">

                           <Link 
                              to={getRoutePath(`/time-off-requests/view/${timeOffRequest.id}`)}
                              className="text-[#009efb] hover:text-[#0089db]"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </Link>

                            <Link 
                              to={getRoutePath(`/time-off-requests/edit/${timeOffRequest.id}`)}
                              className="text-[#009efb] hover:text-[#0089db] p-1 rounded hover:bg-blue-50 transition-colors"
                              title="Edit"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </Link>
                            
                            {showDeleteBtn && (
                              <button
                                onClick={() => handleDelete(String(timeOffRequest.id))}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {currentItems.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-200">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {showMoreAssignToModal?.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity">
          {/* Modal content */}
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative transform transition-all scale-95 animate-fadeIn">
            
            {/* Header */}
            <div className="flex items-center justify-between border-b pb-3 mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Assign To</h2>
              <button
                onClick={() => setShowMoreAssignToModal({ show: false, data: [] })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="text-gray-600 max-h-60 overflow-y-auto pr-1">
              {showMoreAssignToModal?.data?.assign_to &&
              Array.isArray(showMoreAssignToModal?.data?.assign_to) &&
              showMoreAssignToModal?.data?.assign_to.length > 0 ? (
                <div className="space-y-2">
                  {showMoreAssignToModal?.data?.assign_to?.map((value, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition"
                    >
                      <UserGroupIcon className="w-5 h-5 text-gray-400" />
                      <Link
                        to={getRoutePath(`/company-workforces/edit/${value?.id}`)}
                        className="text-blue-600 hover:underline"
                      >
                        {value?.first_name} {value?.last_name}
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">No users assigned.</p>
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end gap-3 border-t pt-3">
             
              <button
                onClick={() => {
                   
                  setShowMoreAssignToModal({ show: false, data: [] });
                }}
                className="flex items-center w-50 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 bg-[#009efb] text-white shadow-md"
              >
                Ok
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default TimeOffRequestsList; 